import { isPricingV2Live } from "./pricing-flags";
import type { Config } from "./types";

export { isPricingV2Live } from "./pricing-flags";

/**
 * Evaluated once at module load: the flag is a build-time constant in the
 * browser and a deploy-time constant on the server, so `hidden` below makes
 * every existing `!plan.hidden` picker flag-aware. Code that must react to the
 * flag at call time (tests, guards) uses `getSellablePlanKeys` instead.
 */
const PRICING_V2_LIVE = isPricingV2Live();

const TRIAL_PERIOD_DAYS = process.env.NEXT_PUBLIC_TRIAL_PERIOD_DAYS
	? Number.parseInt(process.env.NEXT_PUBLIC_TRIAL_PERIOD_DAYS)
	: 30;
export const config = {
	// Internationalization
	i18n: {
		// Whether internationalization should be enabled (if disabled, you still need to define the locale you want to use below and set it as the default locale)
		enabled: true,
		// Define all locales here that should be available in the app
		// You need to define a label that is shown in the language selector and a currency that should be used for pricing with this locale
		locales: {
			en: {
				currency: "USD",
				label: "English",
			},
			de: {
				currency: "USD",
				label: "Deutsch",
			},
		},
		// The default locale is used if no locale is provided
		defaultLocale: "en",
		// The default currency is used for pricing if no currency is provided
		defaultCurrency: "USD",
		// The name of the cookie that is used to determine the locale
		localeCookieName: "NEXT_LOCALE",
	},
	// Organizations
	organizations: {
		// Whether organizations are enabled in general
		enable: true,
		// Whether billing for organizations should be enabled (below you can enable it for users instead)
		enableBilling: true,
		// Whether the organization should be hidden from the user (use this for multi-tenant applications)
		hideOrganization: false,
		// Should users be able to create new organizations? Otherwise only admin users can create them
		enableUsersToCreateOrganizations: false,
		// Whether users should be required to be in an organization. This will redirect users to the organization page after sign in
		requireOrganization: true,
		// Define forbidden organization slugs. Make sure to add all paths that you define as a route after /app/... to avoid routing issues
		forbiddenOrganizationSlugs: [
			"new-organization",
			"admin",
			"settings",
			"ai-demo",
			"organization-invitation",
			"spyglasses",
		],
	},
	// Users
	users: {
		// Whether billing should be enabled for users (above you can enable it for organizations instead)
		enableBilling: false,
		// Whether you want the user to go through an onboarding form after signup (can be defined in the OnboardingForm.tsx)
		enableOnboarding: true,
	},
	// Authentication
	auth: {
		// Whether users should be able to create accounts (otherwise users can only be by admins)
		enableSignup: true,
		// Whether users should be able to sign in with a magic link
		enableMagicLink: true,
		// Whether users should be able to sign in with a social provider
		enableSocialLogin: false,
		// Whether users should be able to sign in with a passkey
		enablePasskeys: true,
		// Whether users should be able to sign in with a password
		enablePasswordLogin: true,
		// where users should be redirected after the sign in
		redirectAfterSignIn: "/app",
		// where users should be redirected after logout
		redirectAfterLogout: "/",
		// where users should be redirected after stopping impersonation
		redirectAfterStopImpersonation: "/app/admin",
		// how long a session should be valid
		sessionCookieMaxAge: 60 * 60 * 24 * 30,
	},
	// Mails
	mails: {
		// the from address for mails
		from: "hello@mail.spyglasses.io",
	},
	// Frontend
	ui: {
		// the themes that should be available in the app
		enabledThemes: ["light", "dark"],
		// the default theme
		defaultTheme: "light",
		// the saas part of the application
		saas: {
			// whether the saas part should be enabled (otherwise all routes will be redirect to the marketing page)
			enabled: process.env.NEXT_PUBLIC_SAAS_ENABLED === "true",
			// whether the sidebar layout should be used
			useSidebarLayout: true,
		},
		// the marketing part of the application
		marketing: {
			// whether the marketing features should be enabled (otherwise all routes will be redirect to the saas part)
			enabled: true,
		},
	},
	// Storage
	storage: {
		// define the name of the buckets for the different types of files
		bucketNames: {
			avatars: process.env.NEXT_PUBLIC_AVATARS_BUCKET_NAME ?? "avatars",
			logos: process.env.NEXT_PUBLIC_LOGOS_BUCKET_NAME ?? "logos",
			reports: process.env.NEXT_PUBLIC_REPORTS_BUCKET_NAME ?? "reports",
			accounting_reports:
				process.env.NEXT_PUBLIC_ACCOUNTING_REPORTS_BUCKET_NAME ??
				"accounting_reports",
			"site-audit-screenshots":
				process.env.NEXT_PUBLIC_SCREENSHOTS_BUCKET_NAME ??
				"site-audit-screenshots",
			exports: process.env.NEXT_PUBLIC_EXPORTS_BUCKET_NAME ?? "exports",
			// Private: YouTube Optimizer speech audio. 100 MB per object, audio
			// types only; deleted when a run completes (24 h after a failure).
			"video-audio":
				process.env.NEXT_PUBLIC_VIDEO_AUDIO_BUCKET_NAME ??
				"video-audio",
		},
	},
	contactForm: {
		// whether the contact form should be enabled
		enabled: true,
		// the email to which the contact form messages should be sent
		to: "hello@mail.spyglasses.io",
		// the subject of the email
		subject: "Message from Spyglasses website",
	},
	// Payments
	payments: {
		// define the products that should be available in the checkout
		plans: {
			lifetime: {
				generation: "v1",
				isLegacy: true,
				hidden: true,
				prices: [
					{
						type: "one-time",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_LIFETIME as string,
						amount: 499,
						currency: "USD",
					},
				],
			},

			// ===== NEW CREDIT-BASED PLANS (Organization-level billing) =====
			// Pay-As-You-Go Credits
			paygCredits: {
				minPurchaseAmount: 15, // Minimum $15 purchase
				prices: [
					{
						type: "one-time",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_USAGE_CREDITS as string,
						amount: 1, // $1 per credit (quantity set by user)
						currency: "USD",
					},
				],
			},

			// Legacy Agency Plans (with bundled credits) - Hidden from new signups
			// Kept for existing customers who are on these plans
			agencyCore: {
				generation: "v1",
				hidden: true, // Hidden from pricing page
				isLegacy: true, // Marked as legacy for tracking
				credits: 350, // Monthly credit allocation (legacy bundled model)
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_MONTHLY as string,
						interval: "month",
						amount: 499,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 1357, // 10% discount from 3×$499
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_ANNUALLY as string,
						interval: "year",
						amount: 4788, // 20% discount from 12×$499
						currency: "USD",
					},
				],
			},
			// v1 Brand ($29). Frozen: existing subscribers keep this price and
			// these allowances. Hidden from pickers once v2 pricing is live.
			brand: {
				generation: "v1",
				hidden: PRICING_V2_LIVE,
				usageBased: true, // Overage invoicing allowed beyond bundled credits
				credits: 30, // Monthly bundled credits (1-month expiry, use-or-lose)
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_MONTHLY as string,
						interval: "month",
						amount: 29,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 78,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_ANNUALLY as string,
						interval: "year",
						amount: 288,
						currency: "USD",
					},
				],
			},
			// Usage-Based Agency Plans (bundled credits + overage invoicing)
			// For new signups starting with universal trial
			// v1 Agency Core ($199). Frozen like `brand` above.
			agencyCoreUsageBased: {
				generation: "v1",
				hidden: PRICING_V2_LIVE,
				usageBased: true, // Overage invoicing allowed beyond bundled credits
				credits: 300, // Monthly bundled credits (1-month expiry, use-or-lose)
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_USAGE_MONTHLY as string,
						interval: "month",
						amount: 199,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_USAGE_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 537, // 10% discount from 3×$199
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_USAGE_ANNUALLY as string,
						interval: "year",
						amount: 1910, // 20% discount from 12×$199
						currency: "USD",
					},
				],
			},
			// ===== OCTOBER 2026 PLANS (v2) =====
			// Sellable only when NEXT_PUBLIC_PRICING_V2=1. Never delete these keys
			// (or any plan key): price-id → plan resolution strands subscribers
			// whose key disappears. `intervalCount` is explicit on every price;
			// the pricing table's savings badge compares against the
			// `intervalCount === 1` monthly price. Allowances live in
			// `@repo/payments` PLAN_ENTITLEMENTS; `credits` here must match its
			// `monthlyCredits` (asserted in plan-entitlements.test.ts).
			brandV2: {
				generation: "v2",
				hidden: !PRICING_V2_LIVE,
				usageBased: true,
				credits: 60,
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_V2_MONTHLY as string,
						interval: "month",
						intervalCount: 1,
						amount: 79,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_V2_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 213, // $71/mo
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_BRAND_V2_ANNUALLY as string,
						interval: "year",
						intervalCount: 1,
						amount: 756, // $63/mo
						currency: "USD",
					},
				],
			},
			agencyCoreV2: {
				generation: "v2",
				hidden: !PRICING_V2_LIVE,
				usageBased: true,
				credits: 300,
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_V2_MONTHLY as string,
						interval: "month",
						intervalCount: 1,
						amount: 349,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_V2_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 942, // $314/mo
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_CORE_V2_ANNUALLY as string,
						interval: "year",
						intervalCount: 1,
						amount: 3348, // $279/mo
						currency: "USD",
					},
				],
			},
			agencyPro: {
				generation: "v2",
				hidden: !PRICING_V2_LIVE,
				usageBased: true,
				credits: 720,
				prices: [
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_PRO_MONTHLY as string,
						interval: "month",
						intervalCount: 1,
						amount: 699,
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_PRO_QUARTERLY as string,
						interval: "month",
						intervalCount: 3,
						amount: 1887, // $629/mo
						currency: "USD",
					},
					{
						type: "recurring",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AGENCY_PRO_ANNUALLY as string,
						interval: "year",
						intervalCount: 1,
						amount: 6708, // $559/mo
						currency: "USD",
					},
				],
			},
			// Enterprise: Custom pricing
			enterprise: {
				isEnterprise: true,
			},
			aiReportUnlock: {
				hidden: true, // Hidden from pricing table since it's purchased directly from reports
				prices: [
					{
						type: "one-time",
						productId: process.env
							.NEXT_PUBLIC_PRICE_ID_AI_REPORT_UNLOCK as string,
						amount: 15, // $15 one-time unlock
						currency: "USD",
					},
				],
			},
		},
	},
} as const satisfies Config;

export type { Config };

/** Every plan key in config. Never shrinks: removing a key strands subscribers. */
export type PlanKey = keyof (typeof config)["payments"]["plans"];

/** Sellable under both pricing generations. */
const ALWAYS_SELLABLE_PLAN_KEYS: readonly PlanKey[] = [
	"paygCredits",
	"enterprise",
	"aiReportUnlock",
];

/**
 * The plan keys that can be bought right now.
 *
 * - Flag on: the v2 subscription plans.
 * - Flag off: the v1 plans that are not legacy (`brand`, `agencyCoreUsageBased`).
 *
 * `paygCredits`, `enterprise` and `aiReportUnlock` are always included. Plans
 * outside this set still resolve by price id everywhere (webhooks, credit
 * grants, plan detection); they are only withheld from pickers and checkout.
 *
 * Reads the flag at call time (unlike `plan.hidden`, which is fixed at load).
 */
export function getSellablePlanKeys(): PlanKey[] {
	const generation = isPricingV2Live() ? "v2" : "v1";
	const plans = config.payments.plans as Config["payments"]["plans"];
	return (Object.keys(plans) as PlanKey[]).filter((key) => {
		if (ALWAYS_SELLABLE_PLAN_KEYS.includes(key)) return true;
		const plan = plans[key];
		return plan.generation === generation && !plan.isLegacy;
	});
}

export function isPlanSellable(planKey: string): boolean {
	return (getSellablePlanKeys() as string[]).includes(planKey);
}
